import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { RootState } from "../../../redux/store";
import { useAppSelector } from "../../../redux/hooks";
import {
  useGetExercisesQuery,
  useDeleteExerciseMutation,
  useGetOrganisationsQuery,
} from "../../../services";
import { selectOrganisationId } from "../../../redux/organisationReducer";
import { ApiEndpoints } from "../../../core";
import { ExerciseRow } from "../../../types";
import { adminRoutes } from "../../../routers/AdminRouters";
import { toast } from "../../../core/toast";

const ExerciseList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const organisationId = useAppSelector(selectOrganisationId);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  const [page, setPage] = useState(1);
  const [allExercises, setAllExercises] = useState<any[]>([]);
  const limit = 15;
  const [showThumbPreview, setShowThumbPreview] = useState(false);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState("");

  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  // Debounce search query
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const { data: orgData, isLoading: isOrgLoading } = useGetOrganisationsQuery();
  const selectedOrgObj = (
    (orgData as any)?.data ||
    (orgData as any) ||
    []
  ).find((org: any) => String(org?._id) === String(organisationId));
  const isPremiumOrg = selectedOrgObj?.level?.toUpperCase() === "PREMIUM";
  const {
    data: exercisesResponse,
    isLoading,
    isError,
    isFetching,
  } = useGetExercisesQuery({
    name: debouncedSearch,
    type: filterType === "all" ? undefined : filterType,
    page,
    limit,
  });

  // Reset list when search or filter changes
  React.useEffect(() => {
    setPage(1);
    setAllExercises([]);
  }, [debouncedSearch, filterType]);

  // Transform and append new results
  React.useEffect(() => {
    if (exercisesResponse) {
      const list = Array.isArray(exercisesResponse?.results)
        ? exercisesResponse.results
        : Array.isArray(exercisesResponse?.result)
          ? exercisesResponse.result
          : [];

      const transformed = list.map((exercise: any) => {
        const baseUrl =
          exercise?.type === "CUSTOM"
            ? ApiEndpoints.customWorkoutThumbnailURL
            : ApiEndpoints.defaultWorkoutThumbnailURL;

        const thumbnailUrl = exercise?.thumbnail.key
          ? `${baseUrl}/${exercise.thumbnail.key}`
          : "";

        const videoBaseUrl =
          exercise?.type === "CUSTOM"
            ? ApiEndpoints.originalCustomWorkoutURL
            : ApiEndpoints.originalDefaultWorkoutURL;

        const videoUrl = exercise?.video?.key
          ? `${videoBaseUrl}/${exercise.video.key}`
          : "";
        return {
          exerciseid: String(exercise?._id ?? exercise?.id ?? ""),
          _id: String(exercise?._id ?? exercise?.id ?? ""),
          code: exercise?.code || "",
          image: thumbnailUrl,
          exerciseName: exercise?.name ?? "",
          muscle: exercise?.muscles?.[0]?.name ?? "",
          equipment: exercise?.equipment?.name ?? "",
          contentType: exercise?.type || "",
          video: videoUrl,
        };
      });

      if (page === 1) {
        setAllExercises(transformed);
      } else {
        setAllExercises((prev) => {
          // Flatten results and filter out duplicates
          const newItems = transformed.filter(
            (newItem: any) =>
              !prev.some(
                (oldItem: any) => oldItem.exerciseid === newItem.exerciseid,
              ),
          );
          return [...prev, ...newItems];
        });
      }
    }
  }, [exercisesResponse, page]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      !isFetching &&
      exercisesResponse?.results?.length === limit
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const HandleCreate = async () => {
    try {
      if (!isPremiumOrg) {
        toast.warning("Purchase premium licence to create exercises");
        return; // Stop execution
      }
      navigate(adminRoutes.exerciseCreate);
    } catch (error) {
      console.error("Failed to create exercise:", error);
    }
  };

  const [deleteExercise, { isLoading: isDeleting }] =
    useDeleteExerciseMutation();

  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<ExerciseRow | null>(null);

  const openDelete = (row: ExerciseRow) => setDeleteTarget(row);
  const closeDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (deleteTarget) {
      try {
        await deleteExercise(deleteTarget.exerciseid).unwrap();
        closeDelete();
      } catch (err) {
        console.error("Failed to delete exercise:", err);
        alert("Failed to delete exercise.");
      }
    }
  };

  useEffect(() => {
    if (isPremiumOrg) {
      console.log(isPremiumOrg, "premium org");
    }
  }, [isPremiumOrg]);
  console.log(isPremiumOrg);
  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const thClass =
    theme === "dark"
      ? "px-4 py-3 text-left text-sm font-bold text-[#F3F3F6] border-b border-[#2C2C2C]"
      : "px-4 py-3 text-left text-sm font-bold text-gray-700 border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-4 py-3 text-sm text-text-table border-b border-[#2C2C2C]"
      : "px-4 py-3 text-sm text-text-table border-b border-gray-200";

  // Filter exercises (search and type are handled by API)
  let filteredRows = allExercises.filter(
    (r: any) => !removedIds.has(r.exerciseid),
  );

  // sidebar width handling (64px collapsed, 256px expanded)
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">Exercise Overview</h1>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
                stroke="#ADADAD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
                stroke="#ADADAD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              to="/"
              className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
            >
              Dashboard
            </Link>
            <span
              className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
            >
              /
            </span>
            <span className="text-[#B6CB24] font-medium">All Exercise</span>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden`}>
        <div
          className={
            theme === "dark"
              ? "px-4 py-3 border-b border-[#2C2C2C] text-gray-200 flex items-center justify-between"
              : "px-4 py-3 border-b border-gray-200 text-gray-800 flex items-center justify-between"
          }
        >
          <span>All Exercise Details</span>

          <div className="flex items-center gap-3">
            {/* Search Input */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="11" cy="11" r="8" strokeWidth="1.5" />
                <path
                  d="M21 21l-4.35-4.35"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search in table"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 pr-4 py-1.5 rounded-md text-sm ${
                  theme === "dark"
                    ? "bg-[#0B0B0B] border border-[#2C2C2C] text-gray-300 placeholder-gray-500"
                    : "bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                style={{ minWidth: "200px" }}
              />
            </div>

            {/* Filter Dropdown */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`px-5 py-1.5 rounded-[10px] border border-[#79747E] text-sm ${
                theme === "dark"
                  ? "bg-[#2E2E2E] text-gray-300"
                  : "bg-gray-50 text-gray-900"
              }`}
            >
              <option value="all">Filter by list</option>
              <option value="CUSTOM">Custom</option>
              <option value="DEFAULT">Default</option>
            </select>

            {/* Create New Exercise Button */}
            <button
              onClick={HandleCreate}
              // disabled={!isPremiumOrg} // Physically disables the button if not premium
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] transition-colors ${
                !isPremiumOrg
                  ? "opacity-50 cursor-not-allowed" // Applies when disabled
                  : "hover:bg-[#B6CB24]/10" // Applies when active
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#B6CB24"
                  strokeWidth="1.5"
                />
                <path
                  d="M12 8V16M16 12H8"
                  stroke="#B6CB24"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Create New Exercise
            </button>
          </div>
        </div>

        <div
          className="overflow-x-auto max-h-[calc(110vh-320px)] overflow-y-auto"
          onScroll={handleScroll}
        >
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Id
                </th>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Image
                </th>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Exercise Name
                </th>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Muscle Group
                </th>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Equipment
                </th>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Content Type
                </th>
                <th
                  className={`${thClass} ${
                    theme === "dark" ? "bg-[#1E1E1E]" : "bg-white"
                  }`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={7}>
                    Loading...
                  </td>
                </tr>
              )}

              {isError && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={7}>
                    Failed to load exercises.
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                filteredRows.map((r: any) => {
                  return (
                    <tr key={r.exerciseid}>
                      <td className={tdClass}>{r.code}</td>
                      <td className={tdClass}>
                        <img
                          src={`${r.image}` || "  "}
                          alt={r.exerciseName}
                          className="w-16 h-16 object-cover rounded-md"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "";
                          }}
                          onClick={() => {
                            if (!r.image) return;
                            setThumbPreviewUrl(r.image);
                            setShowThumbPreview(true);
                          }}
                        />
                      </td>
                      <td className={tdClass}>{r.exerciseName}</td>
                      <td className={tdClass}>{r.muscle.toLowerCase()}</td>
                      <td className={tdClass}>{r.equipment.toLowerCase()}</td>
                      <td className={tdClass}>{r.contentType.toLowerCase()}</td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-2">
                          <button
                            className="p-1.5 rounded hover:bg-gray-700/50"
                            onClick={() => {
                              if (!r.video) {
                                alert("No video available");
                                return;
                              }
                              setVideoPreviewUrl(r.video);
                              setShowVideoPreview(true);
                            }}
                          >
                            <svg
                              width="25"
                              height="21"
                              viewBox="0 0 25 21"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M22.7629 0H1.45295C0.653828 0 0 0.653828 0 1.45295V17.4354C0 18.2345 0.653828 18.8884 1.45295 18.8884H7.04682V20.0314H5.62292C5.35655 20.0314 5.13861 20.2493 5.13861 20.5157C5.13861 20.7821 5.35655 21 5.62292 21H7.53113H16.6847H18.5929C18.8593 21 19.0773 20.7821 19.0773 20.5157C19.0773 20.2493 18.8593 20.0314 18.5929 20.0314H17.169V18.8884H22.7629C23.562 18.8884 24.2159 18.2345 24.2159 17.4354V1.45295C24.2159 0.653828 23.562 0 22.7629 0ZM16.2004 20.0314H8.01545V18.8884H16.2004V20.0314ZM23.2472 17.4354C23.2472 17.7018 23.0293 17.9197 22.7629 17.9197H16.6847H7.53113H1.45295C1.18658 17.9197 0.968634 17.7018 0.968634 17.4354V1.45295C0.968634 1.18658 1.18658 0.968635 1.45295 0.968635H22.7629C23.0293 0.968635 23.2472 1.18658 23.2472 1.45295V17.4354Z"
                                fill="white"
                              />
                              <path
                                d="M12.1077 4.77551C8.96447 4.77551 6.02951 6.47062 4.45064 9.20217C4.36346 9.35231 4.36346 9.53635 4.45064 9.68649C6.03435 12.418 8.96932 14.1131 12.1077 14.1131C15.2461 14.1131 18.1859 12.418 19.7647 9.68649C19.8519 9.53635 19.8519 9.35231 19.7647 9.20217C18.1859 6.47062 15.2509 4.77551 12.1077 4.77551ZM12.1077 13.1445C9.4052 13.1445 6.87222 11.7352 5.4338 9.44433C6.87222 7.15351 9.4052 5.74415 12.1077 5.74415C14.8102 5.74415 17.3432 7.15351 18.7816 9.44433C17.3432 11.7352 14.8102 13.1445 12.1077 13.1445Z"
                                fill="#C6D92C"
                              />
                              <path
                                d="M12.1079 6.82349C10.6647 6.82349 9.48779 8.00038 9.48779 9.44364C9.48779 10.8869 10.6647 12.0638 12.1079 12.0638C13.5512 12.0638 14.7281 10.8869 14.7281 9.44364C14.7281 8.00038 13.5512 6.82349 12.1079 6.82349ZM12.1079 11.0952C11.1974 11.0952 10.4564 10.3542 10.4564 9.44364C10.4564 8.53313 11.1974 7.79212 12.1079 7.79212C13.0185 7.79212 13.7595 8.53313 13.7595 9.44364C13.7595 10.3542 13.0185 11.0952 12.1079 11.0952Z"
                                fill="white"
                              />
                            </svg>
                          </button>

                          {/* Edit Button */}
                          <button
                            className="p-1.5 rounded hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() =>
                              navigate(`/exercise/${r.exerciseid}`)
                            }
                            disabled
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M16.2141 4.98239L17.6158 3.58063C18.39 2.80646 19.6452 2.80646 20.4194 3.58063C21.1935 4.3548 21.1935 5.60998 20.4194 6.38415L19.0176 7.78591M16.2141 4.98239L10.9802 10.2163C9.93493 11.2616 9.41226 11.7842 9.05637 12.4211C8.70047 13.058 8.3424 14.5619 8 16C9.43809 15.6576 10.942 15.2995 11.5789 14.9436C12.2158 14.5877 12.7384 14.0651 13.7837 13.0198L19.0176 7.78591M16.2141 4.98239L19.0176 7.78591"
                                stroke="#B6CB24"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M21 12C21 16.2426 21 18.364 19.682 19.682C18.364 21 16.2426 21 12 21C7.75736 21 5.63604 21 4.31802 19.682C3 18.364 3 16.2426 3 12C3 7.75736 3 5.63604 4.31802 4.31802C5.63604 3 7.75736 3 12 3"
                                stroke="#B6CB24"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>

                          {/* Delete Button */}
                          <button
                            className="p-1.5 rounded hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => openDelete(r)}
                            disabled
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                                fill="url(#paint0_linear_315_4502)"
                              />
                              <path
                                d="M9.5 16.5V10.5"
                                stroke="url(#paint1_linear_315_4502)"
                                stroke-width="1.5"
                                stroke-linecap="round"
                              />
                              <path
                                d="M14.5 16.5V10.5"
                                stroke="url(#paint2_linear_315_4502)"
                                stroke-width="1.5"
                                stroke-linecap="round"
                              />
                              <defs>
                                <linearGradient
                                  id="paint0_linear_315_4502"
                                  x1="12"
                                  y1="1.25"
                                  x2="12"
                                  y2="22.75"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stop-color="#D92626" />
                                  <stop offset="1" stop-color="#AD1F1F" />
                                </linearGradient>
                                <linearGradient
                                  id="paint1_linear_315_4502"
                                  x1="10"
                                  y1="10.5"
                                  x2="10"
                                  y2="16.5"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stop-color="#D92626" />
                                  <stop offset="1" stop-color="#AD1F1F" />
                                </linearGradient>
                                <linearGradient
                                  id="paint2_linear_315_4502"
                                  x1="15"
                                  y1="10.5"
                                  x2="15"
                                  y2="16.5"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stop-color="#D92626" />
                                  <stop offset="1" stop-color="#AD1F1F" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {isFetching && page > 1 && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={7}>
                    Loading more...
                  </td>
                </tr>
              )}

              {!isLoading && !isError && filteredRows.length === 0 && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={7}>
                    No exercises found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            theme === "dark" ? "bg-black/60" : "bg-black/40"
          }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
              theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
            }`}
          >
            <button
              onClick={closeDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>

            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${
                  theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#B6CB24">
                  <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete</h3>
              <p
                className={`${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-6`}
              >
                Are you sure you want to delete this exercise?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm and delete"}
                </button>
                <button
                  type="button"
                  onClick={closeDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showVideoPreview && videoPreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className={`rounded-lg p-6 max-w-4xl w-full mx-4 ${
              theme === "dark"
                ? "bg-[#1E1E1E] border border-[#2C2C2C]"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                Video Preview
              </h3>
              <button
                type="button"
                onClick={() => setShowVideoPreview(false)}
                className={`p-1 rounded hover:opacity-80 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
                aria-label="Close preview"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                src={videoPreviewUrl}
                controls
                className="w-full max-h-[70vh]"
                style={{ objectFit: "contain" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowVideoPreview(false)}
                className={`px-6 py-2 rounded-lg font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showThumbPreview && thumbPreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className={`rounded-lg p-6 max-w-4xl w-full mx-4 ${
              theme === "dark"
                ? "bg-[#1E1E1E] border border-[#2C2C2C]"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                Thumbnail Preview
              </h3>
              <button
                type="button"
                onClick={() => setShowThumbPreview(false)}
                className={`p-1 rounded hover:opacity-80 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
                aria-label="Close preview"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="relative bg-black rounded-lg overflow-hidden flex justify-center">
              <img
                src={thumbPreviewUrl}
                alt="Thumbnail Preview"
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowThumbPreview(false)}
                className={`px-6 py-2 rounded-lg font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseList;
